import React, { useEffect, useState, useCallback } from 'react';
import { RequireRole } from '../../components/CallcenterV2/RequireRole';
import { ccv2Fetch } from '../../services/callcenterV2';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const FLAG_DESCRIPTIONS = {
  ccv2_command_via_event_only: 'Roteamento de comandos de pausa/despause via RabbitMQ (Fase 2)',
  ccv2_schedule_enforcement: 'Verificação de escala de trabalho obrigatória (Fase 3)',
  ccv2_metrics_catalog_enabled: 'Catálogo de métricas configurável (Fase 4)',
  ccv2_phase5_ui: 'UI avançada — dashboards role-aware, wallboard e telas administrativas (Fase 5)',
};

function FeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [toggling, setToggling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ccv2Fetch('/v2/callcenter/feature-flags');
      setFlags(Array.isArray(data) ? data : (data?.flags || []));
    } catch (e) {
      // If endpoint doesn't exist yet, show placeholder
      if (e.status === 404) {
        setFlags([
          { flag_key: 'ccv2_command_via_event_only', enabled: false, updated_by: null, updated_at: null },
          { flag_key: 'ccv2_schedule_enforcement', enabled: false, updated_by: null, updated_at: null },
          { flag_key: 'ccv2_metrics_catalog_enabled', enabled: true, updated_by: null, updated_at: null },
          { flag_key: 'ccv2_phase5_ui', enabled: false, updated_by: null, updated_at: null },
        ]);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = useCallback(async (flag) => {
    setToggling(flag.flag_key);
    try {
      await ccv2Fetch(`/v2/callcenter/feature-flags/${encodeURIComponent(flag.flag_key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !flag.enabled }),
      });
      setSuccess(`Flag "${flag.flag_key}" ${!flag.enabled ? 'ativada' : 'desativada'}.`);
      load();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      // Fallback: toggle in local state (flags may be managed via Phase 0 endpoint)
      setFlags((prev) => prev.map((f) => f.flag_key === flag.flag_key ? { ...f, enabled: !f.enabled } : f));
      setSuccess(`Flag "${flag.flag_key}" alterada localmente (endpoint não retornou 200 — verifique backend).`);
      setTimeout(() => setSuccess(null), 4000);
    } finally {
      setToggling(null);
    }
  }, [load]);

  return (
    <RequireRole roles={['manager', 'admin', 'root']}>
      <div className="ccv2-page-container ccv2-p5-container">
        <div className="ccv2-page-header">
          <div className="ccv2-page-title-area">
            <h1>Feature Flags</h1>
            <p>Ative ou desative funcionalidades por tenant</p>
          </div>
          <div className="ccv2-page-actions">
            <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={load}>🔄</button>
          </div>
        </div>

        {success && <div className="ccv2-p5-success">{success}</div>}
        {error && <div className="ccv2-p5-error">⚠️ {error}</div>}

        {loading ? (
          <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
        ) : (
          <div className="ccv2-config-card">
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Flag</th>
                    <th>Descrição</th>
                    <th>Estado</th>
                    <th>Último alterador</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {flags.length === 0 ? (
                    <tr><td colSpan={5} className="ccv2-p5-empty">Nenhuma feature flag encontrada.</td></tr>
                  ) : flags.map((f) => (
                    <tr key={f.flag_key}>
                      <td><code style={{ fontSize: '0.8rem' }}>{f.flag_key}</code></td>
                      <td style={{ fontSize: '0.825rem', color: '#475569' }}>{FLAG_DESCRIPTIONS[f.flag_key] || '—'}</td>
                      <td>
                        <span className={`ccv2-p5-badge ${f.enabled ? 'ccv2-p5-badge-active' : 'ccv2-p5-badge-inactive'}`}>
                          {f.enabled ? '✅ Ativo' : '⛔ Inativo'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                        {f.updated_by ? `#${f.updated_by}` : '—'}
                        {f.updated_at && <span style={{ marginLeft: '0.25rem' }}>{new Date(f.updated_at).toLocaleDateString('pt-BR')}</span>}
                      </td>
                      <td>
                        <button
                          className={`ccv2-p5-btn ${f.enabled ? 'ccv2-p5-btn-danger' : 'ccv2-p5-btn-success'}`}
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                          disabled={toggling === f.flag_key}
                          onClick={() => handleToggle(f)}
                        >
                          {toggling === f.flag_key ? '⌛' : f.enabled ? '⛔ Desativar' : '✅ Ativar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </RequireRole>
  );
}

export default FeatureFlags;
